import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';

interface EvalRow {
  template_id?: string | null;
  athlete_video_id?: string | null;
  overall_score?: number | null;
  created_at?: string | null;
}

interface TemplateInfo {
  id: string;
  name: string;
}

interface TemplateEffectivenessCardProps {
  templates: TemplateInfo[];
  evaluations: EvalRow[];
}

interface TemplateRow {
  id: string;
  name: string;
  eligibleVideos: number;
  avgDelta: number;
}

export function TemplateEffectivenessCard({
  templates,
  evaluations,
}: TemplateEffectivenessCardProps) {
  const rows = useMemo((): TemplateRow[] => {
    const byTemplateVideo: Record<string, Record<string, EvalRow[]>> = {};

    for (const ev of evaluations) {
      if (!ev.template_id || !ev.athlete_video_id || ev.overall_score == null) continue;
      if (!byTemplateVideo[ev.template_id]) byTemplateVideo[ev.template_id] = {};
      if (!byTemplateVideo[ev.template_id][ev.athlete_video_id]) {
        byTemplateVideo[ev.template_id][ev.athlete_video_id] = [];
      }
      byTemplateVideo[ev.template_id][ev.athlete_video_id].push(ev);
    }

    return templates
      .map((t) => {
        const videoMap = byTemplateVideo[t.id] ?? {};
        const deltas: number[] = [];

        for (const evals of Object.values(videoMap)) {
          if (evals.length < 2) continue;
          const sorted = [...evals].sort(
            (a, b) =>
              new Date(a.created_at ?? 0).getTime() -
              new Date(b.created_at ?? 0).getTime()
          );
          const first = sorted[0].overall_score!;
          const last = sorted[sorted.length - 1].overall_score!;
          deltas.push(last - first);
        }

        return {
          id: t.id,
          name: t.name,
          eligibleVideos: deltas.length,
          avgDelta: deltas.length > 0
            ? deltas.reduce((a, b) => a + b, 0) / deltas.length
            : 0,
        };
      })
      .filter((r) => r.eligibleVideos > 0)
      .sort((a, b) => b.avgDelta - a.avgDelta)
      .slice(0, 10);
  }, [templates, evaluations]);

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Template Effectiveness</CardTitle>
        </CardHeader>
        <CardContent>
          <p
            style={{
              fontSize: '13px',
              color: 'var(--color-text-muted)',
              textAlign: 'center',
              padding: '24px 0',
            }}
          >
            Not enough data yet — athletes need at least 2 evaluations per template.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Template Effectiveness</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                <th
                  className="text-left pb-3 pr-4 font-medium text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Template
                </th>
                <th
                  className="text-center pb-3 pr-4 font-medium text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Submissions
                </th>
                <th
                  className="text-right pb-3 font-medium text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Avg Improvement
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isPositive = row.avgDelta > 0.5;
                const isNegative = row.avgDelta < -0.5;
                const deltaColor = isPositive
                  ? 'var(--color-accent)'
                  : isNegative
                  ? 'var(--color-accent-red)'
                  : 'var(--color-text-muted)';

                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom:
                        idx < rows.length - 1
                          ? '1px solid var(--color-border-subtle)'
                          : 'none',
                    }}
                  >
                    <td className="py-3 pr-4">
                      <span
                        className="text-xs font-medium"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {row.name}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-center">
                      <span
                        className="text-xs"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {row.eligibleVideos}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: deltaColor }}
                      >
                        {row.avgDelta >= 0 ? '+' : ''}
                        {row.avgDelta.toFixed(1)} pts
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default TemplateEffectivenessCard;
